import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { settingsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Upload } from 'lucide-react';

const SettingsForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    tagline: '',
    description: '',
    phone: '',
    email: '',
    instagram: '',
    facebook: '',
    whatsapp: '',
    aboutText: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [workshopImageFile, setWorkshopImageFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await settingsAPI.get();
      setFormData({
        companyName: settings.companyName || '',
        tagline: settings.tagline || '',
        description: settings.description || '',
        phone: settings.phone || '',
        email: settings.email || '',
        instagram: settings.instagram || '',
        facebook: settings.facebook || '',
        whatsapp: settings.whatsapp || '',
        aboutText: settings.aboutText || '',
      });
    } catch (error) {
      toast.error('Error al cargar configuración');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });

      if (logoFile) data.append('logo', logoFile);
      if (heroImageFile) data.append('heroImage', heroImageFile);
      if (workshopImageFile) data.append('workshopImage', workshopImageFile);

      await settingsAPI.update(data);
      toast.success('Configuración actualizada');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Error al guardar configuración');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <Link to="/admin/dashboard">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Dashboard
          </Button>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Configuración del Sitio</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Nombre de la Empresa
                  </label>
                  <Input
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className="border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Tagline
                  </label>
                  <Input
                    name="tagline"
                    value={formData.tagline}
                    onChange={handleInputChange}
                    className="border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Teléfono
                  </label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Email
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    WhatsApp
                  </label>
                  <Input
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleInputChange}
                    placeholder="+5491167667569"
                    className="border-stone-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Instagram URL
                  </label>
                  <Input
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    className="border-stone-300"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Facebook URL
                  </label>
                  <Input
                    name="facebook"
                    value={formData.facebook}
                    onChange={handleInputChange}
                    className="border-stone-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Descripción Principal
                </label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="border-stone-300 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Texto "Sobre Nosotros"
                </label>
                <Textarea
                  name="aboutText"
                  value={formData.aboutText}
                  onChange={handleInputChange}
                  rows={4}
                  className="border-stone-300 resize-none"
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-stone-800">Imágenes</h3>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Logo
                  </label>
                  <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-stone-700 rounded-lg border-2 border-stone-300 border-dashed cursor-pointer hover:bg-stone-50">
                    <Upload className="w-8 h-8 mb-2 text-stone-400" />
                    <span className="text-sm">
                      {logoFile ? logoFile.name : 'Click para subir logo'}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => setLogoFile(e.target.files[0])}
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Imagen Hero
                  </label>
                  <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-stone-700 rounded-lg border-2 border-stone-300 border-dashed cursor-pointer hover:bg-stone-50">
                    <Upload className="w-8 h-8 mb-2 text-stone-400" />
                    <span className="text-sm">
                      {heroImageFile ? heroImageFile.name : 'Click para subir imagen hero'}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => setHeroImageFile(e.target.files[0])}
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Imagen Taller
                  </label>
                  <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-stone-700 rounded-lg border-2 border-stone-300 border-dashed cursor-pointer hover:bg-stone-50">
                    <Upload className="w-8 h-8 mb-2 text-stone-400" />
                    <span className="text-sm">
                      {workshopImageFile ? workshopImageFile.name : 'Click para subir imagen taller'}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => setWorkshopImageFile(e.target.files[0])}
                    />
                  </label>
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-clay-primary hover:bg-clay-dark text-white py-6"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/dashboard')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsForm;
